import { Component, OnInit, inject, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngxs/store';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { InventoryService } from '../../services/inventory.service';
import { PlantMetadata } from '../../models/stock-movement-metadata.model';
import { StockMovementDto } from '../../models/stock-movement.model';
import { TableComponent, TableConfig, Alert, AlertService, AlertType, PaginatorFetcher, PaginatorFilters } from '@michelin/theme';
import { map, debounceTime, distinctUntilChanged } from 'rxjs';
import { TranslationState } from '../../../../shared/state/TranslationState';

@Component({
  selector: 'app-stock-movement-history',
  templateUrl: './stock-movement-history.component.html',
  styleUrls: ['./stock-movement-history.component.scss', '../../inventory-shared.scss'],
  standalone: false
})
export class StockMovementHistoryComponent implements OnInit {
  @ViewChild(TableComponent, { static: false }) tableRef: TableComponent<StockMovementDto>;

  public store = inject(Store);
  public fb = inject(FormBuilder);
  public translationService = inject(TranslationService);
  public inventoryService = inject(InventoryService);
  public alertService = inject(AlertService);

  searchForm: FormGroup;

  // Metadata for dropdowns
  rawMaterialStockOptions: string[] = [];
  rawMaterialTypeOptions: string[] = [];
  stockMovementTypeOptions: string[] = [];
  plantOptions: PlantMetadata[] = [];
  plantOptionsFormatted: string[] = [];
  plantCodeToFkMap: { [plantCode: string]: string } = {};
  serialNumbersByPlant: { [plantFk: string]: string[] } = {};
  serialNumberOptions: string[] = [];
  loading = false;

  // Table data
  stockMovements: StockMovementDto[] = [];
  currentPage = 1;
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50, 100];
  rawMaterialStockMap: { [plantFk: string]: string[] } = {};

  constructor() {
    this.searchForm = this.fb.group({
      rawMaterialType: [''],
      serialNumber: [''],
      description: [''],
      stockMovementType: [''],
      plant: ['']
    });

    // Subscribe to form value changes to trigger search automatically
    // Debounce for 500ms to avoid excessive API calls while typing
    this.searchForm.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
      this.triggerSearch();
    });

    // When plant changes, update serial number and raw material stock dropdowns and reset current selection
    this.searchForm.get('plant')!.valueChanges.subscribe((plantCode: string) => {
      const plantFk = this.plantCodeToFkMap[plantCode] || '';
      this.updateSerialNumberOptions(plantFk);
      this.updateRawMaterialStockOptions(plantFk);
      this.searchForm.get('serialNumber')!.setValue('', { emitEvent: false });
    });
  }

  tableConfig: TableConfig<StockMovementDto> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom',
      pageLabelOptions: {
        firstPageLabel: '',
        lastPageLabel: '',
        nextPageLabel: '',
        previousPageLabel: '',
        itemsPerPageLabel: this.translationService.translate('pageLabelOptionText')
      }
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };

  fetcher: PaginatorFetcher<StockMovementDto> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;

    const formValues = this.searchForm.value;
    const plantFk = this.plantCodeToFkMap[formValues.plant] || formValues.plant || '';
    const searchFilters = {
      rawMaterialType: formValues.rawMaterialType,
      serialNumber: formValues.serialNumber,
      rawMaterial: formValues.description,
      stockMovementType: formValues.stockMovementType,
      plantSetupFk: plantFk
    };

    return this.inventoryService.searchStockMovements(searchFilters, filters.page, filters.limit).pipe(
      map((response) => {
        if (response != null && response.data != null) {
          this.stockMovements = response.data.stockMovements || [];
          return {
            items: this.stockMovements,
            count: response.data.pagination?.totalRecords || 0
          };
        } else {
          this.alertService.showAlert({
            type: AlertType.strong,
            content: this.translationService.translate('tryAgainText')
          } as Alert);
          return {
            items: [],
            count: 0
          };
        }
      })
    );
  };

  ngOnInit(): void {
    this.loadMetadata();
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        plantCode: {
          cell: (item: StockMovementDto) => item.plantCode || '-',
          header: this.translationService.translate('plantCodeText')
        },
        rawMaterialType: {
          cell: (item: StockMovementDto) => item.rawMaterialType || '-',
          header: this.translationService.translate('rawMaterialTypeText')
        },
        serialNumber: {
          cell: (item: StockMovementDto) => item.serialNumber || '-',
          header: this.translationService.translate('serialNumberText')
        },
        rawMaterial: {
          cell: (item: StockMovementDto) => item.rawMaterial || '-',
          header: this.translationService.translate('descriptionText')
        },
        qty: {
          cell: (item: StockMovementDto) => `${item.qty || 0} ${item.qtyUnit || ''}`,
          header: this.translationService.translate('quantityText')
        },
        stockMovementType: {
          cell: (item: StockMovementDto) => item.stockMovementType || '-',
          header: this.translationService.translate('stockMovementTypeText')
        },
        updateDate: {
          cell: (item: StockMovementDto) => (item.updateDate ? item.updateDate : '-'),
          header: this.translationService.translate('updateDateText')
        },
        updateUser: {
          cell: (item: StockMovementDto) => item.updateUser || '-',
          header: this.translationService.translate('updateUserText')
        },
        comment: {
          cell: (item: StockMovementDto) => item.comment || '-',
          header: this.translationService.translate('commentText')
        }
      };
      this.tableConfig.onColumnsChange = (columns: string[]) => {
        Object.keys(this.tableConfig.columns).forEach((key) => {
          this.tableConfig.columns[key].display = columns.includes(key);
        });
      };
    });
  }
  setDefaultValues(): void {
    // Optionally set default values for the form if needed
    // For now, just reset the form
    this.searchForm.reset(
      {
        rawMaterialType: '',
        serialNumber: '',
        description: '',
        stockMovementType: '',
        plant: ''
      },
      { emitEvent: false }
    );
    this.updateSerialNumberOptions('');
    this.updateRawMaterialStockOptions('');
  }

  loadMetadata(): void {
    this.loading = true;

    this.inventoryService.getStockMovementMetadata().subscribe({
      next: (response) => {
        const data = response.data;
        this.plantOptions = data?.plants || [];

        // Create plant code to FK mapping and formatted options for dropdown
        this.plantCodeToFkMap = {};
        const plantCodes = this.plantOptions.map((plant) => {
          this.plantCodeToFkMap[plant.plantCode] = plant.plantSetupPk;
          return plant.plantCode;
        });
        // Remove duplicates from plant codes
        this.plantOptionsFormatted = [...new Set(plantCodes)];

        // Remove duplicates from raw material types
        this.rawMaterialTypeOptions = [...new Set(data?.rawMaterialType || [])];

        // Sort each plant's raw material stock descriptions and remove duplicates within each plant
        this.rawMaterialStockMap = {};
        if (data?.rawMaterialStock) {
          Object.keys(data.rawMaterialStock).forEach((plantFk) => {
            const plantStocks = data.rawMaterialStock[plantFk] || [];
            this.rawMaterialStockMap[plantFk] = [...new Set(plantStocks)].sort((a, b) => this.naturalSort(a, b));
          });
        }

        // Get current plant FK from plant code
        const currentPlantCode = this.searchForm.get('plant')!.value;
        const currentPlantFk = this.plantCodeToFkMap[currentPlantCode] || '';
        this.updateRawMaterialStockOptions(currentPlantFk);

        // Remove duplicates from stock movement types
        this.stockMovementTypeOptions = [...new Set(data?.stockMovementType || [])];

        // Sort each plant's serial numbers and remove duplicates within each plant
        this.serialNumbersByPlant = {};
        if (data?.serialNumbersByPlant) {
          Object.keys(data.serialNumbersByPlant).forEach((plantFk) => {
            const plantSerialNumbers = data.serialNumbersByPlant[plantFk] || [];
            this.serialNumbersByPlant[plantFk] = [...new Set(plantSerialNumbers)].sort((a, b) =>
              a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
            );
          });
        }
        // Get all unique serial numbers across all plants (remove duplicates and sort)
        const allSerialNumbers = Object.values(this.serialNumbersByPlant).reduce((acc, sns) => acc.concat(sns), [] as string[]);
        this.serialNumberOptions = [...new Set(allSerialNumbers)].sort((a, b) =>
          a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
        this.setDefaultValues();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading stock movement metadata:', error);
        this.loading = false;
      }
    });
  }

  // Helper method for natural/alphanumeric sorting
  private naturalSort(a: string, b: string): number {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }

  updateRawMaterialStockOptions(plantFk: string): void {
    if (plantFk) {
      this.rawMaterialStockOptions = (this.rawMaterialStockMap[plantFk] || []).slice().sort((a, b) => this.naturalSort(a, b));
    } else {
      // No plant selected — show all raw material stocks across all plants (remove duplicates)
      const allStocks = Object.values(this.rawMaterialStockMap || {}).reduce((acc, stocks) => acc.concat(stocks), [] as string[]);
      this.rawMaterialStockOptions = [...new Set(allStocks)].sort((a, b) => this.naturalSort(a, b));
    }
  }

  triggerSearch(): void {
    if (this.tableRef) {
      this.tableRef.resetFilters();
    }
  }

  updateSerialNumberOptions(plantFk: string): void {
    if (plantFk) {
      this.serialNumberOptions = (this.serialNumbersByPlant[plantFk] || [])
        .slice()
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    } else {
      // No plant selected — show all serial numbers across all plants (remove duplicates)
      const allSerialNumbers = Object.values(this.serialNumbersByPlant).reduce((acc, sns) => acc.concat(sns), [] as string[]);
      this.serialNumberOptions = [...new Set(allSerialNumbers)].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
      );
    }
  }

  clearSelection(): void {
    this.searchForm.reset({
      rawMaterialType: '',
      serialNumber: '',
      description: '',
      stockMovementType: '',
      plant: ''
    });
    // No need to manually trigger search, valueChanges will handle it
  }

  refreshData(): void {
    // Reload metadata and trigger a fresh search
    this.loadMetadata();
    this.triggerSearch();
  }
}
